import { useSyncExternalStore } from 'react';

/**
 * Whose Bunker the platform frame expects (cispracs/web#16, 19.10.2026). The browser may keep a
 * Bunker session of another account than the one signed in on the platform (a shared computer,
 * an account switch on the platform): the frame then showed that account's chats and status.
 *
 * The host says whom it frames Bunker for: `{ v: 1, type: 'cispracs:platform-account', platformId }`
 * (the platform user id, the Cispracs ID `sub`). Bunker signed in as anyone else shows its
 * sign-in instead of the chats and asks the host to pass the page through its sign-in, which
 * signs the right account in.
 *
 * A host that says nothing (an older shell build) is not waited for longer than WAIT_MS: Bunker
 * then works as before. Only a string id counts; the host sends it once it knows its user.
 */
export const WAIT_MS = 1500;
const MAX_ID = 128;

/** undefined: not known yet; null: the host did not say (older shell); string: the expected id. */
let expected: string | null | undefined;
let timer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

function set(next: string | null): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (next === expected) return;
  expected = next;
  listeners.forEach((cb) => cb());
}

/** The id from a host message, or null when it is not one. */
export function readPlatformAccount(data: Record<string, unknown>): string | null {
  const id = data.platformId;
  return typeof id === 'string' && id.length > 0 && id.length <= MAX_ID ? id : null;
}

/** At boot inside the frame: wait for the host to say, at most WAIT_MS. */
export function startFrameAccountWait(): void {
  if (expected !== undefined || timer) return;
  timer = setTimeout(() => {
    timer = null;
    if (expected === undefined) set(null);
  }, WAIT_MS);
}

/** The host named the account it frames Bunker for. */
export function setFrameAccount(platformId: string): void {
  set(platformId);
}

/**
 * The frame's account check for the signed-in user. `pending` while the host may still say;
 * `mismatch` when the host expects another account; `ok` otherwise (also outside the frame).
 */
export function frameAccountVerdict(
  embedded: boolean,
  value: string | null | undefined,
  platformId: string | null | undefined,
): 'pending' | 'ok' | 'mismatch' {
  if (!embedded) return 'ok';
  if (value === undefined) return 'pending';
  if (value === null) return 'ok';
  return platformId === value ? 'ok' : 'mismatch';
}

export function useFrameAccount(): string | null | undefined {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => expected,
  );
}

/** For tests. */
export function resetFrameAccountForTest(): void {
  if (timer) clearTimeout(timer);
  timer = null;
  expected = undefined;
  listeners.clear();
}
